import React, { useEffect, useState, useContext, useRef } from 'react';
import { Box, Typography, Stack, TextField, Button, CircularProgress, Tabs, Tab } from '@mui/material';
import chatApi from '../api/chatApi';
import { AuthContext } from '../auth/AuthContext';

export default function FascicoloChat({ fascicoloId }) {
  const { user } = useContext(AuthContext);

  const [selectedTab, setSelectedTab] = useState('normal'); // Normale
  const [roomId, setRoomId] = useState(null);
  const [messages, setMessages] = useState([]);

  const [aiRoomId, setAiRoomId] = useState(null);
  const [aiMessages, setAiMessages] = useState([]);

  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [thinking, setThinking] = useState(false);

  // Paginazione
  const [pageNormal, setPageNormal] = useState(null);
  const [pageAI, setPageAI] = useState(null);
  const [totalPagesNormal, setTotalPagesNormal] = useState(0);
  const [totalPagesAI, setTotalPagesAI] = useState(0);
  const [hasMoreNormal, setHasMoreNormal] = useState(true);
  const [hasMoreAI, setHasMoreAI] = useState(true);

  // Riferimenti agli scroll container
  const normalContainerRef = useRef(null);
  const aiContainerRef = useRef(null);

  const normalEndRef = useRef(null);
  const aiEndRef = useRef(null);

  // =========================
  // INIZIALIZZA CHAT
  // =========================
  useEffect(() => {
    const initChat = async () => {
      try {
        setLoading(true);
        if (!fascicoloId) return;

        // 1️⃣ Normale
        const roomRes = await chatApi.getFascicoloRoom(fascicoloId);
        setRoomId(roomRes.data.id);

        // prendi l’ultima pagina
        const lastPageRes = await chatApi.getMessages(roomRes.data.id, { lastPage: true });
        setMessages(lastPageRes.data.content || []);
        setPageNormal(lastPageRes.data.number);
        setTotalPagesNormal(lastPageRes.data.totalPages);
        setHasMoreNormal(lastPageRes.data.number > 0);

        setTimeout(() => normalEndRef.current?.scrollIntoView({ behavior: 'auto' }), 50);

        // 2️⃣ AI
        const aiRoomRes = await chatApi.getAIFascicoloRoom(fascicoloId);
        setAiRoomId(aiRoomRes.data.id);

        const aiLastPageRes = await chatApi.getMessages(aiRoomRes.data.id, { lastPage: true });
        setAiMessages(aiLastPageRes.data.content || []);
        setPageAI(aiLastPageRes.data.number);
        setTotalPagesAI(aiLastPageRes.data.totalPages);
        setHasMoreAI(aiLastPageRes.data.number > 0);

        setTimeout(() => aiEndRef.current?.scrollIntoView({ behavior: 'auto' }), 50);

      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    initChat();
  }, [fascicoloId]);

  // =========================
  // INVIO MESSAGGIO
  // =========================
  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    const targetRoomId = selectedTab === 'normal' ? roomId : aiRoomId;
    if (!targetRoomId) return;

    try {
      if (selectedTab === 'normal') setLoading(true);
      else setThinking(true);

      const formData = new FormData();
      formData.append(
        "data",
        new Blob([JSON.stringify({ content: newMessage })], { type: "application/json" })
      );

      if (selectedTab !== 'normal') setNewMessage("sto pensando...");

      const res = await chatApi.sendMessage(targetRoomId, formData);

      if (selectedTab === 'normal') setMessages(prev => [...prev, res.data]);
      else {
        setAiMessages(prev => [...prev, res.data.parentMessage, res.data]);
      }

      setNewMessage('');
      setTimeout(() => {
        if (selectedTab === 'normal') normalEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        else aiEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 50);

    } catch (err) {
      console.error(err);
    } finally {
      if (selectedTab === 'normal') setLoading(false);
      else setThinking(false);
    }
  };

  // =========================
  // FETCH MESSAGGI PAGINATI
  // =========================
  const fetchPrevMessages = async (tab) => {
    try {
      if (tab === 'normal') {
        if (!hasMoreNormal || pageNormal <= 0) return;
        const prevPage = pageNormal - 1;
        const res = await chatApi.getMessages(roomId, { page: prevPage });
        setMessages(prev => [...res.data.content, ...prev]);
        setPageNormal(prevPage);
        setHasMoreNormal(prevPage > 0);
      } else {
        if (!hasMoreAI || pageAI <= 0) return;
        const prevPage = pageAI - 1;
        const res = await chatApi.getMessages(aiRoomId, { page: prevPage });
        setAiMessages(prev => [...res.data.content, ...prev]);
        setPageAI(prevPage);
        setHasMoreAI(prevPage > 0);
      }
    } catch (err) {
      console.error("Errore caricamento messaggi precedenti", err);
    }
  };

  // =========================
  // SCROLL HANDLER
  // =========================
  const handleScroll = (e, tab) => {
    const container = e.target;
    if (container.scrollTop === 0) fetchPrevMessages(tab);
  };

  // =========================
  // RENDER MESSAGGI
  // =========================
  const renderMessages = (msgs, tab) => (
    <Stack
      spacing={1}
      sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}
      onScroll={(e) => handleScroll(e, tab)}
      ref={tab === 'normal' ? normalContainerRef : aiContainerRef}
    >
      {msgs.map(m => (
        <Box
          key={m.id}
          sx={{
            p: 1,
            bgcolor: m.authorId === user.id ? '#DCF8C6' : '#F1F0F0',
            borderRadius: 1
          }}
        >
          <Typography variant="body2">{m.senderRole}</Typography>
          <Typography variant="body2">{m.content}</Typography>
          <Typography variant="caption">{new Date(m.createdAt).toLocaleString()}</Typography>
        </Box>
      ))}
      <div ref={tab === 'normal' ? normalEndRef : aiEndRef} />
    </Stack>
  );

  return (
    <Box mt={4} p={2} sx={{ border: '1px solid #ccc', borderRadius: 2 }}>
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}

      <Typography variant="h6" sx={{ mb: 2 }}>Chat Fascicolo</Typography>

      <Tabs value={selectedTab} onChange={(e, val) => setSelectedTab(val)}>
        <Tab label="Chat Contribuente" value="normal" />
        <Tab label="Chat AI" value="ai" />
      </Tabs>

      {selectedTab === 'normal' && renderMessages(messages, 'normal')}
      {selectedTab === 'ai' && renderMessages(aiMessages, 'ai')}

      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          size="small"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Scrivi un messaggio..."
          onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
        />
        <Button variant="contained" onClick={sendMessage}>Invia</Button>
      </Stack>
    </Box>
  );
}
